import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { SingleCaNumberDetails } from '../../../../../@store/products/ca-numbers/ca-numbers.model';

type SpecFieldKey = keyof Pick<
  SingleCaNumberDetails,
  | 'awg'
  | 'stranding'
  | 'wireType'
  | 'supplier'
  | 'finishedDiameter'
  | 'tip'
  | 'die'
  | 'putUp'
  | 'jacketMaterial'
  | 'jacketColor'
  | 'jacketPrinting'
  | 'wallThickness'
  | 'innerDiameter'
  | 'outerDiameter'
>;

@Component({
  selector: 'ciwc-order-specification-builder',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
  ],
  standalone: true,
  templateUrl: './order-specification-builder.component.html',
  styleUrl: './order-specification-builder.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderSpecificationBuilderComponent implements OnInit, OnChanges {
  @Input() parentForm!: FormGroup;
  @Input() details: SingleCaNumberDetails | null = null;
  @Input() viewDisable = false;
  @Input() readonly = false;
  @Output() requestChangeCa = new EventEmitter<void>();

  specFields: { key: SpecFieldKey; label: string }[] = [
    { key: 'awg', label: 'AWG' },
    { key: 'stranding', label: 'Stranding' },
    { key: 'wireType', label: 'Wire Type' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'finishedDiameter', label: 'Finished Diameter' },
    { key: 'tip', label: 'Tip' },
    { key: 'die', label: 'Die' },
    { key: 'putUp', label: 'Put Up' },
    { key: 'jacketMaterial', label: 'Jacket Material' },
    { key: 'jacketColor', label: 'Jacket Color' },
    { key: 'jacketPrinting', label: 'Jacket Printing' },
    { key: 'wallThickness', label: 'Wall Thickness' },
    { key: 'innerDiameter', label: 'Inner Diameter' },
    { key: 'outerDiameter', label: 'Outer Diameter' },
  ];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['details'] && this.details) {
      this.initializeForm();
      this.updateFormWithDetails();
    }
  }

  private initializeForm(): void {
    if (!this.parentForm) return;

    if (!this.parentForm.contains('specification')) {
      this.parentForm.addControl('specification', this.fb.group({}));
    }

    const specGroup = this.parentForm.get('specification') as FormGroup;
    this.specFields.forEach((field) => {
      if (!specGroup.contains(field.key)) {
        specGroup.addControl(field.key, this.fb.array([this.fb.control('', Validators.required)]));
      }
    });
  }

  private updateFormWithDetails(): void {
    if (!this.parentForm || !this.details) return;

    const specGroup = this.parentForm.get('specification') as FormGroup;
    this.specFields.forEach((field) => {
      let arr = specGroup.get(field.key) as FormArray;
      if (!arr) {
        arr = this.fb.array([]);
        specGroup.addControl(field.key, arr);
      } else {
        arr.clear();
      }

      const fieldValue = this.details?.[field.key];
      const values = Array.isArray(fieldValue)
        ? fieldValue.filter((v) => v && v !== '' && v !== null)
        : fieldValue && fieldValue !== '' && fieldValue !== null
          ? [fieldValue]
          : [];

      if (values.length > 0) {
        values.forEach((val: any) =>
          arr.push(
            this.fb.control({ value: val, disabled: this.viewDisable }, Validators.required),
          ),
        );
      } else {
        // Add empty string so checkbox is visible but unchecked
        arr.push(this.fb.control({ value: '', disabled: this.viewDisable }, Validators.required));
      }
    });
  }

  getSpecArray(field: SpecFieldKey): FormArray<FormControl> {
    if (!this.parentForm?.get('specification')) {
      return this.fb.array([]) as FormArray<FormControl>;
    }
    return this.parentForm.get('specification.' + field) as FormArray<FormControl>;
  }

  getSpecFormControl(field: SpecFieldKey, index: number): FormControl {
    const array = this.getSpecArray(field);
    return array.at(index) as FormControl;
  }

  addField(field: SpecFieldKey): void {
    if (this.excludeFieldsForReadonly(field)) return;
    const array = this.getSpecArray(field);
    array.push(this.fb.control({ value: '', disabled: this.viewDisable }, Validators.required));
  }

  removeField(field: SpecFieldKey, index: number): void {
    if (this.excludeFieldsForReadonly(field)) return;
    const array = this.getSpecArray(field);
    if (array.length > 1) {
      array.removeAt(index);
    }
  }

  onReadonlyClick(event: Event, field: SpecFieldKey) {
    if (this.excludeFieldsForReadonly(field)) {
      event.preventDefault();
      event.stopPropagation();
      this.requestChangeCa.emit();
    }
  }

  onAddFieldClick(event: Event, field: SpecFieldKey) {
    if (this.excludeFieldsForReadonly(field)) {
      this.onReadonlyClick(event, field);
      return;
    }

    this.addField(field);
  }

  onRemoveFieldClick(event: Event, field: SpecFieldKey, index: number) {
    if (this.excludeFieldsForReadonly(field)) {
      this.onReadonlyClick(event, field);
      return;
    }
    this.removeField(field, index);
  }

  /**
   * Exclude certain fields from triggering the readonly behavior
   * @param field
   * @returns {boolean}
   */
  private excludeFieldsForReadonly(field: SpecFieldKey): boolean {
    const fieldsToExclude = ['supplier', 'putUp'];

    return this.readonly && !fieldsToExclude.includes(field);
  }
}
